import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Link from '@amzn/meridian/link'
import Table, {
    TableSectionHeader,
} from '@amzn/meridian/table'
import Text from '@amzn/meridian/text'

import { sideBarConfig, SideBarMode } from '@utils/ui-config'
import { openProvideFeedbackModal, openViewFeedbackModal } from '@crossframe/apis/ToolMenu'
import { store } from '@atredux/store'
import { setOpenModalCalledWithMigratedTool, switchToLastNonMigratedTool } from '@atredux/slices/toolSlice'
import { switchToSeawardIFrame } from '@utils/ModalSwitchUtil'
import { isToolSelectMode, isZoneSelectMode } from '@components/SideBar/SideBar'

import { setIsSeawardModalOpen } from '@atredux/slices/iframeSlice'
import { EnvironmentDetail, MasterIndirectTaskService } from '@amzn/ihm-associate-tools-js-lib'
import { StoreIdServiceFor3P } from '@pages/StoreIdSelectionFor3P/utils/StoreIdServiceFor3P'
import { LocationCheckUtils } from '@utils/LocationCheckUtils'
import { fetchDeviceDetailsInfo } from '@components/SideBar/utils/SideBarUtil'
import { setMI } from '@src/redux/slices/atAlertSlice'
import * as RiverService from '@src/services/RiverService'
import { MessageBundle } from '@amzn/arb-tools'
import { useLogout } from '@hooks/useLogout'

interface ISideBarFooterProps {
    showAllTools?: boolean
    setShowAllTools?: (flagSet: boolean) => void
    zone?: string
    isMenuOpen: boolean
    toggleMenu?: () => void
    sideBarMode: SideBarMode
    setChangeLanguageOpen: (open: boolean) => void
    isFrequentlyUsedToolsEnabled: boolean
    t: Function
    bundle: MessageBundle
}

/**
 * Displays the footer options within the sidebar and all
 * subcomponents. Since the sidebar returns a table element, the footer
 * simply returns the required table components to be rendered.
 * It also optionally renders the 'Show all tools' option in footer
 * when tool selection mode is enabled.
 *
 * Usage:
 * <SideBarFooter
 *     showAllTools={true}
 *     setShowAllTools={setShowAllTools}
 *     zone="FOH"
 * />
 *
 * @param {boolean} showAllTools Flag to determine if all tools should be displayed.
 * @param {Function} setShowAllTools Function that toggles all tools/tools for zone.
 * @param {string} zone Currently selected zone.
 * @param {boolean} isFrequentlyUsedToolsEnabled Flag to determine if the frequently used tools feature is enabled.
 */
const SideBarFooter = ({
    showAllTools,
    setShowAllTools,
    zone,
    isMenuOpen,
    toggleMenu,
    sideBarMode,
    setChangeLanguageOpen,
    isFrequentlyUsedToolsEnabled,
    t,
    bundle,
}: Readonly<ISideBarFooterProps>) => {
    const navigate = useNavigate()
    const logout = useLogout()
    const is3Pflow: boolean = EnvironmentDetail.isIhmIdentityBrokerAuthN()
    const isShowReselectStoreButton = useRef(StoreIdServiceFor3P.getNearbyStores().length > 1 || StoreIdServiceFor3P.getNearbyStores().length === 0)
    const toggleProvideFeedbackModal = useCallback(() => {
        store.dispatch(setOpenModalCalledWithMigratedTool())
        switchToSeawardIFrame()
        openProvideFeedbackModal()
        if (isMenuOpen) {
            toggleMenu()
        }
        store.dispatch(setIsSeawardModalOpen(true))
    }, [])
    const toggleViewFeedbackModal = useCallback(() => {
        store.dispatch(setOpenModalCalledWithMigratedTool())
        switchToSeawardIFrame()
        openViewFeedbackModal()
        if (isMenuOpen) {
            toggleMenu()
        }
        store.dispatch(setIsSeawardModalOpen(true))
    }, [])
    // River service
    const isRiverEnabled: boolean = RiverService.isRiverEnabled()
    const openRiver = useCallback(() => {
        console.log('ATBase: Openning river modal...')
        window.location.href = RiverService.getExternalRiverURL()
    }, [])
    // TODO: fix logout redirection
    // Commented out with the Change Language menu item below — see the TODO there.
    // const changeLanguage = useCallback(() => {
    //     setChangeLanguageOpen(true)
    // }, [])

    const reselectStore = useCallback(() => {
        // Clearing storeDetails when reselect store is clicked
        StoreIdServiceFor3P.resetStoreIdServiceFor3P()
        // Hiding the iframe before navigating back to store-selection page
        LocationCheckUtils.hideLoadedIframe()
        navigate('/store-selection')

    }, [])

    const openMasterTaskSelection = useCallback(() => {
        store.dispatch(setMI())
    }, [])

    const isAndroid = EnvironmentDetail.isAndroid()
    const miConfig = MasterIndirectTaskService.getInstance().getConfig()

    // Commented out with the Change Language menu item below — see the TODO there.
    // const _shouldShowChangeLanguageButton = () => {
    //     return true
    // }

    return (
        <Table
            showDividers={true}
        >
            {/* Show All Tools / Show Tools by Zone toggle removed — always show all tools */}
            {isRiverEnabled &&
                <TableSectionHeader>
                    <Link
                        data-atb-id={'side-menu-footer-open-river-link'}
                        type={sideBarConfig.SIDEBAR_LINK_TYPE}
                        onClick={openRiver}
                    >
                        {t(bundle, 'migrated-ihm-seaward-river-button')}
                    </Link>
                </TableSectionHeader>
            }
            {/*
              * TODO: Based on business decision we either enable this or remove this functionality altogether.
              *
              * In-app language selection is hidden for now. SMP is embedded in the localized
              * JWO iOS/Android shells, which pass the locale via query param, so this menu item
              * would let the web view disagree with the surrounding native UI.
              *
              * The `setChangeLanguageOpen` prop and `ChangeLanguageModal` are intentionally left
              * wired up (see ToolLayout) so re-enabling is just uncommenting this block along
              * with `changeLanguage` and `_shouldShowChangeLanguageButton` above.
              */}
            {/* {_shouldShowChangeLanguageButton() &&
                <TableSectionHeader>
                    <Link
                        data-atb-id={'side-menu-footer-change-language-link'}
                        type={sideBarConfig.SIDEBAR_LINK_TYPE}
                        onClick={changeLanguage}
                    >
                        {t(bundle, 'ihm-associatetoolsbase-select-locale')}
                    </Link>
                </TableSectionHeader>
            } */}
            {is3Pflow && isShowReselectStoreButton.current &&
            <TableSectionHeader>
                <Link
                    data-atb-id={'side-menu-footer-reselect-store'}
                    type={sideBarConfig.SIDEBAR_LINK_TYPE}
                    onClick={reselectStore}
                >
                    {t(bundle, 'jsma-web-sidebar-reselectStore')}
                </Link>
            </TableSectionHeader>
            }
            {EnvironmentDetail.isAndroid() && miConfig &&
            <TableSectionHeader>
                <Link
                    data-atb-id={'side-menu-footer-select-master-task'}
                    type={sideBarConfig.SIDEBAR_LINK_TYPE}
                    onClick={openMasterTaskSelection}
                >
                    {t(bundle, 'jsma-web-masterTask-selectLink')}
                </Link>
            </TableSectionHeader>
            }
            <TableSectionHeader>
                <Link
                    data-atb-id={'side-menu-footer-logout-link'}
                    type={sideBarConfig.SIDEBAR_LINK_TYPE}
                    onClick={logout}
                >
                    {t(bundle, 'migrated-ihm-seaward-logout-button')}
                </Link>
            </TableSectionHeader>
            {isAndroid && _getDeviceDetailsFooter(t, bundle)}
        </Table>
    )
}

const _getFooterHeader = (showAllTools: boolean, t: Function, messageBundle: MessageBundle, setShowAllTools: (flagSet: boolean) => void) => {
    if (showAllTools) {
        return (
            <TableSectionHeader>
                <Link
                    data-atb-id={'side-menu-footer-show-zone-tools-link'}
                    type={sideBarConfig.SIDEBAR_LINK_TYPE}
                    onClick={() => setShowAllTools(false)}
                >
                    {t(messageBundle, 'migrated-ihm-seaward-show-tools-for-zone-button')}
                </Link>
            </TableSectionHeader>
        )
    }

    return (
        <TableSectionHeader>
            <Link
                data-atb-id={'side-menu-footer-show-all-tools-link'}
                type={sideBarConfig.SIDEBAR_LINK_TYPE}
                onClick={() => setShowAllTools(true)}
            >
                {t(messageBundle, 'migrated-ihm-seaward-show-all-tools-button')}
            </Link>
        </TableSectionHeader>
    )
}

/**
 * TODO: promote to a real <DeviceDetailsFooter /> component. This calls hooks but
 * is invoked as a plain function (`{isAndroid && _getDeviceDetailsFooter(...)}`),
 * so its hooks belong to the caller's hook list. It happens to be safe today only
 * because `isAndroid` is constant for the lifetime of the app — if that ever
 * becomes dynamic, the hook order changes between renders and state gets attached
 * to the wrong slot. Pre-existing; passing `t`/`messageBundle` in just made it
 * more visible.
 */
const _getDeviceDetailsFooter = (t: Function, messageBundle: MessageBundle) => {
    const [deviceId, setDeviceId] = useState(null)
    const [appVersion, setAppVersion] = useState(null)
    useEffect(() => {
        fetchDeviceDetailsInfo().then((result) => {
            setDeviceId(result.deviceId)
            setAppVersion(result.appVersion)
        })
    }, [])
    return (
        <TableSectionHeader>
            <Text type="b100">{t(messageBundle, 'jsma-web-sidebar-deviceId', { deviceId })}</Text>
            <Text type="b100">{t(messageBundle, 'jsma-web-sidebar-version', { appVersion })}</Text>
        </TableSectionHeader>
    )
}

export default SideBarFooter
